/**
 * The library, in sections.
 *
 * One grid of twelve games answers no question anybody actually arrives with.
 * The questions people do arrive with are "what can I play on my own", "what
 * can the four of us play", and "what's the poker" - so those are the
 * sections, and everything else falls into one list at the end rather than
 * being carved up for the sake of symmetry.
 *
 * ## Why these and not a tag per game
 *
 * Every line below reads something the catalog already says. A game is
 * solitaire because it seats one, poker because it says it is poker, a
 * four-hander because its minimum and maximum are both four. Nothing here is a
 * second opinion that could drift from the game's own answer, and a new game
 * lands in the right section without anybody remembering to file it.
 *
 * ## The one exception, and why it had to be one
 *
 * Children's Classics reads a declared flag. Nothing in the catalog can be
 * read instead: `family` is MECHANICAL, so Crazy Eights and Cheat are both
 * `shedding` and Go Fish is `matching`, and audience is not a mechanical
 * property. `difficulty` is the near miss worth naming, because it looks like
 * it would serve and does not - it measures how hard a game is to LEARN, which
 * is Easy for anything teachable in two minutes, a future Blackjack included.
 *
 * The flag is a claim that a game IS a children's game, in the genre sense -
 * the thing a child is taught first - and never that a game is suitable for
 * one. The difference is not pedantry. A suitability shelf would have to hold
 * the patiences, since a child can play Klondike; worse, it would turn every
 * other game's ABSENCE into a promise, and that promise breaks the first time
 * a game gets added without anybody thinking about it. A genre shelf says
 * nothing at all about the games not on it, which is a claim we can keep.
 *
 * So the cost of the exception is that somebody has to remember to file a
 * children's game, and the shape of forgetting is that it does not appear.
 * That is the boring failure rather than the alarming one.
 *
 * ## Order
 *
 * The named genres first, then the seat-count group, then the rest. Solitaire
 * leads because it is the only section whose games need nobody else - it is
 * the answer when the alternative is not playing at all.
 *
 * Children's Classics sits with the other named genres. Where exactly does not
 * matter today and that is worth knowing rather than relying on: none of the
 * three games on it can satisfy any other rule - Go Fish, Crazy Eights and
 * Cheat are none of them one-seat, poker, or four-and-only-four - so the
 * first-match-wins arbitration never runs and the shelf takes only from the
 * catch-all. A future children's game that IS four-handed would be the first
 * to make this ordering mean something.
 */

/**
 * Whether a game answers what somebody typed.
 *
 * Name and blurb, not name alone: at twelve games the useful search is the one
 * that finds Klondike when you type "solitaire" and Hearts when you type
 * "tricks", and neither word is in either name. A stricter match would be
 * right for a catalogue of thousands and is wrong for one you can read in a
 * glance - the failure it protects against, a flood of irrelevant hits, cannot
 * happen here.
 *
 * An empty query matches everything, so the caller never has to special-case
 * "not searching".
 */
export function matchesQuery(
  game: { readonly name: string; readonly blurb: string },
  query: string,
): boolean {
  const wanted = query.trim().toLowerCase();
  if (wanted === '') return true;
  return (
    game.name.toLowerCase().includes(wanted) || game.blurb.toLowerCase().includes(wanted)
  );
}

/** Only what a section needs to know. Keeps this testable without a catalog. */
interface Shelvable {
  readonly family: string;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  /**
   * Optional, because the server may be older than this client - see the wire
   * type in `net/session.ts`. Absent reads the same as false, which is what
   * makes an un-redeployed server produce a page with no children's shelf
   * rather than a broken one.
   */
  readonly childrensClassic?: boolean | undefined;
}

export interface Shelf<T> {
  /**
   * How the URL names this shelf, so "See All" is a link and the back button
   * works. Stable: the heading is copy and may be reworded, this may not.
   */
  readonly id: string;
  /** The heading, or null for the opening grid, which has the page's lede. */
  readonly title: string | null;
  readonly games: T[];
}

/**
 * A section's rule, in the order they are tried.
 *
 * First match wins, so a game appears once: Texas Hold'em is poker rather than
 * also being in the general list, and a future one-player poker would be
 * solitaire rather than both.
 */
const SHELVES: ReadonlyArray<{
  id: string;
  title: string;
  holds: (game: Shelvable) => boolean;
}> = [
  {
    id: 'solitaire',
    title: 'Solitaire',
    // The seat count, not the `patience` family: a one-player game from some
    // other family still belongs here, and that is the thing a player is
    // actually asking about.
    holds: (game) => game.maxPlayers === 1,
  },
  {
    id: 'poker',
    title: 'Poker',
    holds: (game) => game.family === 'poker',
  },
  {
    id: 'childrens-classics',
    /*
     * Named as a genre, and that is load-bearing rather than copy.
     *
     * "For Kids" reads as a suitability label - a parent scanning it would
     * take the shelf to mean these are the safe ones, and therefore take the
     * other nine to mean something too. "Children's Classics" says what it
     * actually knows: these are the games children are taught. The id is
     * stable and the title may be reworded, but not back towards the other
     * meaning.
     */
    title: "Children's Classics",
    holds: (game) => game.childrensClassic === true,
  },
  {
    id: '4-player',
    title: '4-Player',
    holds: (game) => game.minPlayers === 4 && game.maxPlayers === 4,
  },
];

/** The catch-all, named only when something is above it. */
const REST = { id: 'everything-else', title: 'Everything Else' };

/**
 * Sort games into sections, dropping any section nothing landed in.
 *
 * The first section comes back titled `null` when it is the only one: a single
 * heading over a single grid says nothing the page has not already said.
 */
export function shelve<T extends Shelvable>(games: readonly T[]): Shelf<T>[] {
  const shelves: Shelf<T>[] = SHELVES.map((shelf) => ({
    id: shelf.id,
    title: shelf.title,
    games: [],
  }));
  const rest: T[] = [];

  for (const game of games) {
    const index = SHELVES.findIndex((shelf) => shelf.holds(game));
    if (index < 0) rest.push(game);
    else (shelves[index] as Shelf<T>).games.push(game);
  }

  const filled = shelves.filter((shelf) => shelf.games.length > 0);
  if (rest.length > 0) filled.push({ id: REST.id, title: REST.title, games: rest });

  if (filled.length === 1) {
    const only = filled[0] as Shelf<T>;
    return [{ id: only.id, title: null, games: only.games }];
  }
  return filled;
}
